// Read-side offline cache for the module-06 floor screens (spec 06 §4 floor
// rows; doctrine rule 2 "offline behavior where the spec says so").
//
// The OUTBOX makes writes survive a dead zone. That is only half of an offline
// screen: a count worksheet the operator cannot READ is a worksheet they cannot
// fill in, and §3.4 step 3 explicitly says counting is "offline-tolerant". So
// every floor screen persists the payload it last loaded ONLINE, keyed by
// entity, and renders from that snapshot when the network is gone — with the
// snapshot's age on screen, always, because a worksheet from yesterday is a
// different fact from a worksheet from a minute ago.
//
// TWO RULES THIS FILE ENFORCES, both of which are spec constraints rather than
// caching preferences:
//
//  * WHAT THE SERVER OMITTED STAYS OMITTED. The blind-count worksheet is served
//    WITHOUT `expected_qty_base` (§5 row 38 — the field is ABSENT, not null).
//    The cache round-trips through `JSON.stringify`, which drops nothing and
//    invents nothing, so a cached blind worksheet is exactly as blind as the
//    response was. There is no merge step anywhere that could reintroduce an
//    expected value from an earlier, differently-authorised read.
//  * MONEY IS NEVER CACHED HERE. The floor screens read no money field at all
//    (§7 gives floor_worker no `inventory.cost.read`), and nothing in this file
//    goes near one. The entry points below take whole response payloads, which
//    for the floor endpoints carry no visible cost.
//
// Storage is localStorage rather than IndexedDB: these are small, disposable
// snapshots whose loss costs a refresh, not a shift's work. The WRITE queue is
// the thing that must be transactional, and that is IndexedDB (outbox-store.ts).

const PREFIX = "assessly.floor.read.v1:";

// THE CACHE BELONGS TO THE LIVE SESSION (D-635).
//
// Sign-out purges the cache and then navigates to /login, and until that
// navigation commits the floor screen that was open is still mounted. A read
// in flight when the operator tapped sign-out resolves into it; the root
// layout swapping its floor branch for the auth branch remounts the outlet
// while the router still renders the old match, and the remounted screen
// persists the payload the QueryClient still holds. Each of those wrote the
// outgoing operator's unit card straight back after the purge (M06-S13 leg 4,
// staging, `58a0b4b0`), and any later caller could do the same.
//
// So the purge also CLOSES the cache, in the same synchronous step, and while
// it is closed every write here, snapshot or draft, is dropped whoever makes
// it. Ruled once at the storage layer rather than per caller: no screen, hook
// or listener can put anything back. A sign-in door reopens it
// (`openFloorReadCache`), and a page load starts open, because a fresh page
// holds nothing from the session before it.
let closed = false;

export interface CachedRead<T> {
  readonly value: T;
  /** Epoch ms of the successful online read this snapshot came from. */
  readonly cachedAt: number;
}

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** Cache key for one entity read, e.g. `count:{id}` / `transfer:{id}`. */
export function readCacheKey(kind: string, id: string): string {
  return `${kind}:${id}`;
}

export function loadRead<T>(key: string): CachedRead<T> | undefined {
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const raw = storage.getItem(PREFIX + key);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const record = parsed as { value?: unknown; cachedAt?: unknown };
    if (record.value === undefined || typeof record.cachedAt !== "number") return undefined;
    return { value: record.value as T, cachedAt: record.cachedAt };
  } catch {
    return undefined;
  }
}

export function saveRead(key: string, value: unknown, now: number = Date.now()): void {
  if (closed) return;
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    storage.setItem(PREFIX + key, JSON.stringify({ value, cachedAt: now }));
  } catch {
    // Quota or private mode. The screen still renders from memory this session;
    // the honest consequence (no offline read after a reload) is surfaced by the
    // chrome's durability warning, not swallowed here.
  }
}

export function dropRead(key: string): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    storage.removeItem(PREFIX + key);
  } catch {
    // nothing to do
  }
}

/**
 * Drop every floor snapshot and draft on this device, and close the cache
 * until the next sign-in (D-635).
 *
 * Called on sign-out. The origin isolates TENANTS, but not the two operators
 * who share one tablet across a shift change: without this, worker B opens the
 * app and reads worker A's cached worksheet — line ids, serials and counted
 * values B holds no grant for — and B's screen renders A's half-typed manifest.
 * Closing it is what keeps it empty: a screen still mounted behind the
 * sign-out can no longer write A's reads back (see `closed` above).
 */
export function clearFloorReadCache(): void {
  closed = true;
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && (key.startsWith(PREFIX) || key.startsWith(DRAFT_PREFIX)))
        doomed.push(key);
    }
    for (const key of doomed) storage.removeItem(key);
  } catch {
    // nothing to do
  }
}

/**
 * Reopen the cache for a new session. Called by the in-app sign-in doors
 * (routes/login.tsx, routes/reset-password.tsx); the rule over every session
 * door is pinned in lib/__tests__/session-reset.test.ts.
 */
export function openFloorReadCache(): void {
  closed = false;
}

// ---------------------------------------------------------------------------
// Drafts — the work that has not reached the outbox yet
// ---------------------------------------------------------------------------
//
// The outbox is transactional and kill-safe. The HOUR OF TYPING in front of it
// was not: every tick, quantity and variance reason on a 20-line manifest, and
// every count value not yet saved, lived in `useState`. A phone that locks and
// gets evicted, an OS tab kill, a back-swipe mid-unload — and the lot is gone
// with no warning. An operator who ticked 18 of 20 lines and lost them does not
// use the app again.
//
// Drafts are deliberately SEPARATE from the read snapshots: a draft is the
// operator's own unsent input and is cleared the moment it is enqueued, whereas
// a snapshot is the server's answer and is refreshed, not consumed.

const DRAFT_PREFIX = "assessly.floor.draft.v1:";

export function loadDraft(key: string): unknown {
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const raw = storage.getItem(DRAFT_PREFIX + key);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return parsed === null ? undefined : parsed;
  } catch {
    return undefined;
  }
}

export function saveDraft(key: string, value: unknown): void {
  if (closed) return;
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    storage.setItem(DRAFT_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota. The draft simply does not survive a reload; the enqueued work does.
  }
}

export function dropDraft(key: string): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    storage.removeItem(DRAFT_PREFIX + key);
  } catch {
    // nothing to do
  }
}

/**
 * Bucket a snapshot age into an i18n key + count, mirroring the catalogue floor
 * cache's convention so both banners read the same way. Pure: the age label is
 * testable without a clock.
 */
export function snapshotAgeParts(ageMs: number): { key: string; n: number } {
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return { key: "inventory.floor.cache.ageJustNow", n: 0 };
  if (minutes < 60) return { key: "inventory.floor.cache.ageMinutes", n: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: "inventory.floor.cache.ageHours", n: hours };
  return { key: "inventory.floor.cache.ageDays", n: Math.floor(hours / 24) };
}
